import React, { useState, useMemo } from "react";
import styles from "./teacherTable.module.css";
import ZeroData from "../../zeroData/zeroData";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";

const TeacherTable = ({ teacherData = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [showPopover, setShowPopover] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' | 'desc'

  const validTeacherData = useMemo(() => {
    return teacherData
      .slice()
      .filter((teacher) =>
        (teacher.teacherName || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        // ordena pelo teacherName (que estamos usando como "Id do Docente")
        const cmp = (a.teacherName || "").localeCompare(b.teacherName || "");
        return sortOrder === "asc" ? cmp : -cmp;
      });
  }, [teacherData, searchTerm, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(validTeacherData.length / itemsPerPage),
    [validTeacherData.length, itemsPerPage]
  );

  const currentTableData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return validTeacherData.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, validTeacherData]);

  const handlePageClick = (page) => setCurrentPage(page);

  if (!validTeacherData.length) {
    return (
      <ZeroData>
        <h4>Você não enviou dados de professores.</h4>
      </ZeroData>
    );
  }

  return (
    <div className={styles.teacherTableContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Professores</h2>
          <div style={{ position: "relative" }}>
            <img
              src={InfoIcon}
              alt="Informação"
              onClick={() => setShowPopover(!showPopover)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                verticalAlign: "middle",
                display: "inline-block",
              }}
            />
            {showPopover && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "100%",
                  transform: "translate(0, -50%)",
                  marginLeft: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  padding: "10px",
                  zIndex: 100,
                  width: "220px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  borderRadius: "4px",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px" }}>
                  Lista dos docentes com informações agregadas sobre desempenho acadêmico, presença e aprovação dos alunos.
                </p>
                <button
                  onClick={() => setShowPopover(false)}
                  style={{
                    marginTop: "8px",
                    fontSize: "10px",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    color: "#0962AD",
                  }}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          {/* Busca */}
          <div className={styles.searchWrapper}>
            <img src={SearchIcon} alt="Buscar" className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
            />
          </div>

          {/* Select de ordenação */}
          <div>
            <select
              className={styles.orderWrapper}
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="desc">Ordem Decrescente</option>
              <option value="asc">Ordem Crescente</option>
            </select>
          </div>
        </div>
      </div>

      <table className={styles.teacherTable}>
        <thead>
          <tr>
            <th>Id do Docente</th>
            <th>Total de Alunos</th>
            <th>Nota Final Média</th>
            <th>Frequência Média</th>
            <th>Taxa de Aprovação</th>
            <th>Total de Carga Horária</th>
            <th>Aulas Ativas</th>
          </tr>
        </thead>
        <tbody>
          {currentTableData.map((teacher, idx) => (
            <tr key={idx}>
              <td>{teacher.teacherName}</td>
              <td>{teacher.teacherTotalStudents}</td>
              <td>{Number(teacher.teacherAvgFinalGrade).toFixed(2)}</td>
              <td>{Number(teacher.teacherAvgFrequency).toFixed(2)}</td>
              <td>{Number(teacher.teacherApprovalRate).toFixed(2)}</td>
              <td>{teacher.totalHours}</td>
              <td>{teacher.activeClasses}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.pagination}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            className={page === currentPage ? styles.activePage : ""}
            onClick={() => handlePageClick(page)}
          >
            {page}
          </button>
        ))}
      </div>
    </div>
  );
};

export default TeacherTable;
